import { ReactElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heading } from '../../../components/Heading';
import { Paragraph } from '../../../components/Paragraph';
import { ChevronLeftIcon } from '@navikt/aksel-icons';
import classes from './TenantFeaturesPage.module.css';
import { Routes } from '../../../enums/Routes';
import { useGetTenantFeaturesQuery } from '../../../hooks/queries/useGetTenantFeaturesQuery';
import { useAddTenantFeatureMutation } from '../../../hooks/mutations/useAddTenantFeatureMutation';
import { useRemoveTenantFeatureMutation } from '../../../hooks/mutations/useRemoveTenantFeatureMutation';
import { GhostLoader } from '../../../components/GhostLoader/GhostLoader';
import { Alert } from '../../../components/Alert/Alert';
import { ContainerCard } from '../../../components/ContainerCard/ContainerCard';
import { formatNorwegianDate } from '../../../utils/dateFormatters';

const ERROR_MESSAGE = 'Kunne ikke laste feature-data. Prøv igjen senere.';
const NOT_FOUND_MESSAGE = 'Kunde ikke funnet.';

const ALL_FEATURES = [
  { key: 'PSYCHOLOGICAL_SAFETY', label: 'Psykologisk trygghet' },
  { key: 'CUSTOM_SURVEYS', label: 'Egne undersøkelser' },
  { key: 'WORK_ENVIRONMENT', label: 'Arbeidsmiljø' },
  { key: 'ACTION_PLANS', label: 'Handlingsplaner' },
  { key: 'HR_ANALYTICS', label: 'HR Analytics' },
  { key: 'INTEGRATIONS', label: 'Integrasjoner' },
  { key: 'CONTENT_SYSTEM', label: 'Innholdssystem' },
  { key: 'SSO', label: 'Single Sign-On (SSO)' },
  { key: 'SAFETY_ROUNDS', label: 'Vernerunder' },
];

type FeatureToggleProps = {
  tenantId: number;
  featureKey: string;
  featureLabel: string;
  isEnabled: boolean;
  enabledAt?: string;
};

const FeatureToggle = ({
  tenantId,
  featureKey,
  featureLabel,
  isEnabled,
  enabledAt,
}: FeatureToggleProps): ReactElement => {
  const addFeatureMutation = useAddTenantFeatureMutation();
  const removeFeatureMutation = useRemoveTenantFeatureMutation();

  const handleToggle = () => {
    if (isEnabled) {
      removeFeatureMutation.mutate({
        tenantId,
        feature: featureKey,
      });
    } else {
      addFeatureMutation.mutate({
        tenantId,
        feature: featureKey,
      });
    }
  };

  const isLoading =
    (addFeatureMutation.isPending && addFeatureMutation.variables?.feature === featureKey) ||
    (removeFeatureMutation.isPending && removeFeatureMutation.variables?.feature === featureKey);

  return (
    <div className={classes.featureItem}>
      <div className={classes.featureInfo}>
        <Paragraph size="md" className={classes.featureLabel}>
          {featureLabel}
        </Paragraph>
        {isEnabled && enabledAt && (
          <Paragraph size="sm" className={classes.featureMeta}>
            Aktivert {formatNorwegianDate(enabledAt)}
          </Paragraph>
        )}
      </div>
      <button
        type="button"
        className={`${classes.toggleButton} ${isEnabled ? classes.toggleActive : classes.toggleInactive}`}
        onClick={handleToggle}
        disabled={isLoading}
      >
        {isLoading ? '...' : isEnabled ? 'Deaktiver' : 'Aktiver'}
      </button>
    </div>
  );
};

export const TenantFeaturesPage = (): ReactElement => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const parsedTenantId = tenantId ? parseInt(tenantId, 10) : null;

  const { data, isLoading, error, isError } = useGetTenantFeaturesQuery(parsedTenantId);

  if (isLoading) {
    return (
      <div className={classes.container}>
        <GhostLoader style={{ height: '200px' }} />
      </div>
    );
  }

  if (isError || !data || !parsedTenantId) {
    return (
      <div className={classes.container}>
        <Alert variant="error" size="md">
          <Heading as="h3">{error ? ERROR_MESSAGE : NOT_FOUND_MESSAGE}</Heading>
        </Alert>
      </div>
    );
  }

  const enabledFeatureKeys = new Set(data.features.map((f) => f.feature));
  const featureMap = new Map(data.features.map((f) => [f.feature, f]));

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Link to={Routes.SystemAdminTenants} className={classes.backLink}>
          <ChevronLeftIcon fontSize="1.5rem" />
          <span>Tilbake til kunder</span>
        </Link>
      </div>

      <div className={classes.titleSection}>
        <Heading as="h1" className={classes.pageTitle}>
          Administrer features
        </Heading>
        <Paragraph size="lg" className={classes.tenantName}>
          {data.tenantName}
        </Paragraph>
      </div>

      <ContainerCard>
        <ContainerCard.Title>Tilgjengelige features</ContainerCard.Title>
        <ContainerCard.Body>
          <div className={classes.featureList}>
            {ALL_FEATURES.map((feature) => {
              const isEnabled = enabledFeatureKeys.has(feature.key);
              const featureData = featureMap.get(feature.key);

              return (
                <FeatureToggle
                  key={feature.key}
                  tenantId={parsedTenantId}
                  featureKey={feature.key}
                  featureLabel={feature.label}
                  isEnabled={isEnabled}
                  enabledAt={featureData?.enabledAt}
                />
              );
            })}
          </div>
        </ContainerCard.Body>
      </ContainerCard>
    </div>
  );
};
